import gsap from "gsap";
import React from "react";

const approachSteps = [
  {
    id: 1,
    stepNumber: "STEP 1",
    title: "Discovery & Vision Sync",
    image: "/new-final-data/home/s1.webp",
    description: `
We sniff and dig each and every detail of your goals and challenges to reveal your hidden potential.`,
    d2: `Your vision becomes our obsession.`,
    fallbackGradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    fallbackText: "Office Scene",
  },
  {
    id: 2,
    stepNumber: "STEP 2",
    title: "Strategy & Creativity ",
    image: "/new-final-data/home/s2.webp",
    description: `We design systems that cut through the noise and help us predict the next big opportunity.`,
    d2: `Our instinct is your advantage.`,
    fallbackGradient:
      "linear-gradient(135deg, #ff9a9e 0%, #fecfef 50%, #fecfef 100%)",
    fallbackText: "Creative Scene",
  },
  {
    id: 3,
    stepNumber: "STEP 3",
    title: "Set the Pack in Motion",
    image: "/new-final-data/home/s3.webp",
    description: `We create workflows that adapt, endure and drive momentum balancing creativity & practicality.`,
    d2: `Our duality gives you the edge.`,
    fallbackGradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    fallbackText: "Team Results",
  },
];
const HomeApproachSection = () => {
  const [currentStep, setCurrentStep] = React.useState(0);
  const card1 = React.useRef(null);
  const card2 = React.useRef(null);
  const card3 = React.useRef(null);
  const isAnimating = React.useRef(false);

  const clickHandler = (step, e) => {
    // Prevent multiple animations from running simultaneously
    if (isAnimating.current || currentStep === step) return;

    // Check if refs are valid
    if (!card1.current || !card2.current || !card3.current) return;

    isAnimating.current = true;
    setCurrentStep(step);

    // Kill any ongoing animations first
    gsap.killTweensOf([card1.current, card2.current, card3.current]);

    // Create timeline for synchronized animation
    const tl = gsap.timeline({
      duration: 0.6,
      ease: "power3.inOut",
      onComplete: () => {
        isAnimating.current = false;
      },
    });

    // Animate cards based on step
    if (step === 0) {
      tl.to(card1.current, { left: "8%" }, 0)
        .to(card2.current, { left: "35%" }, 0)
        .to(card3.current, { left: "50%" }, 0);
    } else if (step === 1) {
      tl.to(card1.current, { left: "-10%" }, 0)
        .to(card2.current, { left: "35%" }, 0)
        .to(card3.current, { left: "50%" }, 0);
    } else if (step === 2) {
      tl.to(card1.current, { left: "-10%" }, 0)
        .to(card2.current, { left: "8%" }, 0)
        .to(card3.current, { left: "50%" }, 0);
    }
  };

  // Cleanup animations on unmount
  React.useEffect(() => {
    return () => {
      gsap.killTweensOf([card1.current, card2.current, card3.current]);
    };
  }, []);

  return (
    <>
      {/* Mobile & Tablet Layout - Simple Vertical Cards */}
      <div className="xl:hidden min-h-screen py-10 w-full px-10 md:px-8 pointer-events-auto">
        <div className="space-y-8 md:space-y-12">
          {approachSteps.map((step, index) => (
            <div
              key={step.id}
              className="dark:bg-black bg-white dark:text-white overflow-hidden "
            >
              {/* Content Section */}
              <div className="py-6 md:p-8 space-y-4">
                <div className="dark:text-zinc-600 text-zinc-400 font-semibold text-sm md:text-md">
                  {step.stepNumber}
                </div>
                <div className="font-bold text-2xl md:text-4xl">
                  {step.title}
                </div>
                <div className="text-lg md:text-xl opacity-60 leading-relaxed">
                  {step.description}
                </div>
                <div className="text-lg md:text-xl opacity-60 leading-relaxed">
                  {step.d2}
                </div>
              </div>

              {/* Image Section */}
              <div className="h-[80vh] w-full relative overflow-hidden">
                <img
                  className="h-full w-full object-cover object-top"
                  src={step.image}
                  alt={step.title}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Desktop XL Layout - Original Animated Design */}
      <div className="hidden xl:block min-h-[130vh] py-10 pt-20  dark:border-none w-full px-35 relative overflow-hidden pointer-events-auto">
        <div
          ref={card1}
          onClick={(e) => clickHandler(0, e)}
          className="absolute border-t border-zinc-300 dark:border-zinc-800 top-20 left-[8%]  z-30 h-full w-[42.5%] dark:bg-black bg-white dark:shadow-white/6 shadow-black/10 shadow-[30px_0px_40px] dark:text-white space-y-4 py-10 pr-6 cursor-pointer"
        >
          <div className="dark:text-zinc-600 text-zinc-400  font-semibold text-md ">
            {approachSteps[0].stepNumber}
          </div>
          <div className="font-bold text-4xl">{approachSteps[0].title}</div>
          <div className="text-md w-[80%] opacity-60 mb-10">
            {approachSteps[0].description}
          </div>
          <div className="h-full w-full relative overflow-hidden">
            <img
              className="h-full w-full object-cover"
              src={approachSteps[0].image}
              alt=""
            />
          </div>
        </div>
        <div
          ref={card2}
          onClick={(e) => clickHandler(1, e)}
          className="absolute border-t border-zinc-300 dark:border-zinc-800 top-20 left-[35%] z-20 h-full w-[42.5%] dark:bg-black bg-white dark:shadow-white/6 shadow-black/10 shadow-[30px_0px_40px] dark:text-white space-y-4 py-10 pr-6 cursor-pointer"
        >
          <div className="dark:text-zinc-600 text-zinc-400 font-semibold text-md pl-10 ">
            {approachSteps[1].stepNumber}
          </div>
          <div className="font-bold text-4xl pl-10">
            {approachSteps[1].title}
          </div>
          <div className="text-md w-[80%] opacity-60 mb-10 pl-10">
            {approachSteps[1].description}
          </div>
          <div className="h-full w-full relative overflow-hidden">
            <img
              className="h-full w-full object-cover"
              src={approachSteps[1].image}
              alt=""
            />
          </div>
        </div>
        <div
          ref={card3}
          onClick={(e) => clickHandler(2, e)}
          className="absolute border-t border-zinc-300 dark:border-zinc-800 top-20 left-1/2  z-10 h-full w-[42.5%] dark:bg-black bg-white dark:text-white space-y-4 py-10 pr-6 cursor-pointer"
        >
          <div className="dark:text-zinc-600 text-zinc-400 font-semibold text-md pl-10 ">
            {approachSteps[2].stepNumber}
          </div>
          <div className="font-bold text-4xl pl-10">
            {approachSteps[2].title}
          </div>
          <div className="text-md w-[80%] opacity-60 mb-10 pl-10">
            {approachSteps[2].description}
          </div>
          <div className="h-full w-full relative overflow-hidden">
            <img
              className="h-full w-full object-cover"
              src={approachSteps[2].image}
              alt=""
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default HomeApproachSection;
